import cn from 'clsx'
import { FC, MouseEvent } from 'react'
import { Link } from 'react-router-dom'

import { Icon, Typography } from '@/components/ui'

import Play from '@/assets/icons/play_arrow.svg'
import Stop from '@/assets/icons/stop.svg'

import { useAuth } from '@/auth/useAuth'
import { ITask } from '@/types/task.interface'
import { formatDate } from '@/utils/formatDate'
import { sortListeners } from '@/utils/sortListeners'

import { useStartStopTranscribing } from '../hooks/useStartStopTranscribing'

import Status from './Status'

interface IProps {
	listeners: ITask[]
}

const TasksList: FC<IProps> = ({ listeners }) => {
	const { startStopTranscribing } = useStartStopTranscribing()
	const { isInt } = useAuth()

	const sortedListeners = [...listeners].sort(sortListeners)

	if (!sortedListeners.length) return

	const handleCopy = (e: MouseEvent<HTMLButtonElement>, source: string) => {
		e.preventDefault()
		e.stopPropagation()

		navigator.clipboard.writeText(source)
	}

	return (
		<div className='flex flex-col gap-2'>
			<div className='flex gap-0.5 py-2 pr-4 w-fit'>
				<p className='text-2xl font-bold'>
					In Progress ({sortedListeners.length})
				</p>
			</div>

			<div>
				{/* Header */}
				<div
					className={cn(
						'grid border-b border-gray-300',
						!isInt
							? 'grid-cols-[135px_200px_1fr_120px_40px]'
							: 'grid-cols-[135px_200px_1fr_120px]'
					)}
				>
					<div className='text-gray-600 text-sm text-left py-2'>Port</div>
					<div className='text-gray-600 text-sm text-left py-2 pl-4'>
						Last event
					</div>
					<div className='text-gray-600 text-sm text-left py-2 pl-4'>
						Audio tracks
					</div>
					<div className='text-gray-600 text-sm text-left py-2 pl-4'>
						Status
					</div>
				</div>

				{/* Rows */}
				<div className='flex flex-col'>
					{sortedListeners.map(task => (
						<Link
							key={task.id}
							to={`/live/${task.id}`}
							className={cn(
								'grid h-14 border-b border-gray-300 transition-colors hover:bg-gray-100',
								!isInt
									? 'grid-cols-[135px_200px_1fr_120px_40px]'
									: 'grid-cols-[135px_200px_1fr_120px]'
							)}
						>
							<div className='text-gray-600 flex items-center'>
								<div className='flex items-center gap-2 w-full'>
									<Typography variant='text'>{task.source}</Typography>

									<button
										type='button'
										onClick={e => handleCopy(e, task.source)}
									>
										<Icon size={16} icon='copy' color='#ADADAD' />
									</button>
								</div>
							</div>

							<div className='text-gray-600 flex items-center pl-4'>
								<Typography variant='text'>
									{formatDate(task.added_at)}
								</Typography>
							</div>

							<div className='text-gray-600 flex items-center pl-4'>
								<Typography variant='text'>{task?.audio_tracks}</Typography>
							</div>

							<div className='flex items-center pl-4'>
								<Status status={task.status} />
							</div>
							{!isInt && (
								<div className='flex items-center pl-4'>
									<button
										className={cn(
											'rounded-full border border-gray-100 bg-white !p-[7px]',
											task.status === 'stopped' &&
												'!pl-[8px] !pr-[5px] !py-[5.5px]'
										)}
										onClick={async e => {
											e.preventDefault()
											e.stopPropagation()
											await startStopTranscribing({
												proxy_name: task.source,
												blocked: task.status !== 'stopped'
											})
										}}
									>
										{task.status === 'stopped' ? <Play /> : <Stop />}
									</button>
								</div>
							)}
						</Link>
					))}
				</div>
			</div>
		</div>
	)
}

export default TasksList
